import { createConfigParametersRepository } from '../../configParametersRepository';
import { identifyAnalyticsClient } from '../utils/analytics-identification';
import { CustomDimensions } from '../types';
import { get } from '../../utils/httpRequest';
import {
  CUSTOM_PARAMS_STORAGE_KEY,
  MKTTRACK_COOKIE_NAME,
} from '../resources/constants';
import { getPageLocation, gtagWrapper } from '../google-analytics/utils';
import { createFirebaseAdapter } from '../firebase/firebaseAdapter';
import { createFirebaseClient } from '../firebase/firebaseClient';
import { BundleId, FieldsObject, TrackPageOptions } from './types';
import { createRuntimeGoogleState } from '../init/ga/state';
import { getBuyPathParam } from '../init/ga/customDimensions';
import { trackEvent } from './events';
import { checkGaInit } from '../init/ga';
import { CustomParams } from '../google-analytics/ga4Types';
import { handlePageError } from './error';

export const trackPageInGa4 = (screen_name: string) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const firebaseAdapter = createFirebaseAdapter();
  const firebaseClient = createFirebaseClient(firebaseAdapter);

  const storedCustomParams = storageClient?.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  );

  firebaseClient.trackScreenView({
    ...storedCustomParams,
    screen_name,
  });

  const page_location = getPageLocation(screen_name);
  gtagWrapper('event', 'page_view', {
    page_location,
    ...storedCustomParams,
  });
  gtagWrapper('event', 'edo_screen_view', {
    page_location,
    screen_name,
    ...storedCustomParams,
    event_callback: identifyAnalyticsClient,
  });
};

const updatePageCustomDimensions = async (params: CustomParams) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();

  const filteredDimensions = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value || value === '')
  );

  storageClient?.updateCustomParamsAndDimensions(filteredDimensions);
};

export const getPageCustomDimensions = async (page: string) => {
  const { getGoogleAnalyticsParameters } = createConfigParametersRepository();
  const { endpoint } = getGoogleAnalyticsParameters();
  if (!endpoint.pageCustomDimensions) return {};
  const url = `${endpoint.pageCustomDimensions}${encodeURIComponent(page)}`;
  try {
    const response: CustomDimensions = await get(url, {
      accept: 'application/json, text/javascript, */*; q=0.01',
    });
    return response;
  } catch (_) {
    return {};
  }
};

const removeAnalyticsSearchInfo = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();

  storageClient?.removeFromStorage('sessionStorage', 'analyticsSearchInfo');
};

const getMktTrackDimension = (): Pick<FieldsObject, 'dimension33'> => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const dimension33 = storageClient?.getCookie(MKTTRACK_COOKIE_NAME);
  return { dimension33 };
};

const getBundleIdFields = (): Pick<FieldsObject, 'dimension57'> => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const bundleId = storageClient?.getFromStorage(
    'sessionStorage',
    'bookingInfo',
    null
  ) as BundleId | null;

  if (bundleId?.shoppingBasketBundle?.id) {
    return {
      dimension57: bundleId.shoppingBasketBundle.id,
    };
  }
  return {};
};

const getMobileFields = (): Pick<FieldsObject, 'dimension49'> => {
  const { getGenericParameters } = createConfigParametersRepository();
  const { flow } = getGenericParameters();
  if (flow === 'mobile') {
    return {
      dimension49:
        (window.navigator.connection && window.navigator.connection.type) ||
        'unknown',
    };
  }
  return {};
};

const getFieldsObjectForPageview = (): FieldsObject => {
  return {
    ...getMktTrackDimension(),
    ...getBundleIdFields(),
    ...getMobileFields(),
  };
};

export const trackPostponedEvents = () => {
  const { getEventsQueue, emptyEventsQueue } = createRuntimeGoogleState();
  const eventsQueue = getEventsQueue();
  eventsQueue?.forEach((event) => trackEvent(event));
  emptyEventsQueue();
};

const trackGoogleAnalyticsPage = async (
  page: string,
  options?: TrackPageOptions
) => {
  const { customDimensions } = options ?? {};
  const { setParameters } = createRuntimeGoogleState();
  removeAnalyticsSearchInfo();
  const pageBeDimensions = await getPageCustomDimensions(page);
  await updatePageCustomDimensions({
    ...getFieldsObjectForPageview(),
    ...pageBeDimensions,
    ...customDimensions,
    screen_name: page,
    url: document.location.href,
    buypath: options?.funnel ? getBuyPathParam() : undefined,
  });
  trackPageInGa4(page);

  setParameters({ hasThePageBeenTracked: true });
  trackPostponedEvents();
};

export const trackPage = async (page: string, options?: TrackPageOptions) => {
  handlePageError(page);
  const { getGoogleState, setParameters } = createRuntimeGoogleState();
  const { lastTrackedPage } = getGoogleState();
  const { error } = options ?? {};
  if (lastTrackedPage === page || error) return;
  await checkGaInit();
  setParameters({ lastTrackedPage: page, hasThePageBeenTracked: false });
  await trackGoogleAnalyticsPage(page, options);
};
